/** Map the consolidated customer portfolio payload into view fields. */

export const PORTFOLIO_DISPLAY_CURRENCIES = ['AED', 'USD', 'EUR', 'GBP'];

export const toAmount = (value) => {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

export const amountOrZero = (value) => toAmount(value) ?? 0;

/** Prefer display-currency figures when the gateway included them. */
export const displayAmount = (localValue, nativeValue) => toAmount(localValue) ?? toAmount(nativeValue);

export const normalizeCustomerPortfolio = (payload) => {
  const source =
    payload && (payload.assets || payload.cashBalances || payload.currencySymbol || payload.totalSum != null)
      ? payload
      : payload?.data ?? {};
  return {
    currency: source?.currencySymbol || '',
    totalSum: toAmount(source?.totalSum),
    totalCash: toAmount(source?.totalCash),
    totalAvailableForWithdrawal: toAmount(source?.totalAvailableForWithdrawal),
    totalAvailableForTrading: toAmount(source?.totalAvailableForTrading),
    totalInvestedCost: toAmount(source?.totalInvestedCost),
    totalInvestedMarketValue: toAmount(source?.totalInvestedMarketValue),
    totalUnrealizedPl: toAmount(source?.totalUnrealizedPl),
    totalUnrealizedPlPercent: source?.totalUnrealizedPlPercent ?? null,
    assets: Array.isArray(source?.assets) ? source.assets : [],
    cashBalances: Array.isArray(source?.cashBalances) ? source.cashBalances : [],
  };
};

export const assetRow = (asset) => {
  const listingCurrency = asset?.currencySymbol || '';
  const displayCurrency = asset?.localCurrencySymbol || listingCurrency;
  return {
    key: `${asset?.assetId || asset?.instrumentId || asset?.symbol || 'asset'}-${asset?.marketId ?? asset?.marketSymbol ?? ''}`,
    symbol: asset?.symbol || '—',
    name: asset?.name || '',
    marketSymbol: asset?.marketSymbol || '',
    logoUrl: asset?.logoUrl || '',
    listingCurrency,
    displayCurrency,
    quantity: asset?.quantity,
    availableQuantity: asset?.availableQuantity,
    reservedQuantity: asset?.reservedQuantity,
    averageCost: displayAmount(asset?.averageCostLocal, asset?.averageCost),
    averageCostNative: toAmount(asset?.averageCost),
    lastPrice: displayAmount(asset?.lastPriceLocal, asset?.lastPrice),
    lastPriceNative: toAmount(asset?.lastPrice),
    totalCost: displayAmount(asset?.totalCostLocal, asset?.totalCost),
    totalCostNative: toAmount(asset?.totalCost),
    marketValue: displayAmount(asset?.totalMarketValueLocal, asset?.totalMarketValue) ?? 0,
    marketValueNative: toAmount(asset?.totalMarketValue),
    unrealizedPl: displayAmount(asset?.unrealizedPlLocal, asset?.unrealizedPl),
    unrealizedPlNative: toAmount(asset?.unrealizedPl),
    unrealizedPlPercent: asset?.unrealizedPlPercentLocal ?? asset?.unrealizedPlPercent ?? null,
    showNative: Boolean(listingCurrency && displayCurrency && listingCurrency !== displayCurrency),
  };
};

export const cashRow = (balance) => ({
  key: String(balance?.currencyId ?? balance?.currencySymbol ?? balance?.currency ?? 'cash'),
  currency: balance?.currencySymbol || balance?.currency || '—',
  logoUrl: balance?.logoUrl || '',
  precision: balance?.precision,
  cashBalance: toAmount(balance?.cashBalance),
  availableForTrading: toAmount(balance?.availableForTrading),
  availableForWithdrawal: toAmount(balance?.availableForWithdrawal),
  totalReserved: toAmount(balance?.totalReserved),
  pendingSettlements: toAmount(balance?.pendingSettlements),
  pendingSettlementsConsumed: toAmount(balance?.pendingSettlementsConsumed),
});

export const listSnapshot = (payload, fallbackCurrency) => {
  const portfolio = normalizeCustomerPortfolio(payload);
  return {
    currency: portfolio.currency || fallbackCurrency || '',
    totalSum: portfolio.totalSum ?? 0,
    marketValue: portfolio.totalInvestedMarketValue ?? 0,
    cash: portfolio.totalCash ?? 0,
    unrealizedPnl: portfolio.totalUnrealizedPl ?? 0,
    unrealizedPct: portfolio.totalUnrealizedPlPercent,
    positionCount: portfolio.assets.length,
    error: false,
  };
};

/** Shape order placement already reads: { positions: [{ symbol, quantity, ... }] }. */
export const positionsFromPortfolio = (payload) => {
  const portfolio = normalizeCustomerPortfolio(payload);
  return {
    positions: portfolio.assets.map((asset) => {
      const row = assetRow(asset);
      return {
        assetId: asset?.assetId || asset?.instrumentId,
        symbol: row.symbol,
        name: row.name,
        marketSlug: row.marketSymbol,
        currencySymbol: row.displayCurrency || portfolio.currency,
        quantity: row.quantity,
        availableQuantity: row.availableQuantity,
        reservedForOrders: row.reservedQuantity,
        averageCost: row.averageCost,
        lastPrice: row.lastPrice,
        totalMarketValue: row.marketValue,
        unrealizedPl: row.unrealizedPl,
        unrealizedPlPercent: row.unrealizedPlPercent,
        isActive: asset?.isActive !== false,
      };
    }),
  };
};

/** Shape order placement already reads: { balances: [{ currency, availableForTrading }] }. */
export const balancesFromPortfolio = (payload) => {
  const portfolio = normalizeCustomerPortfolio(payload);
  const balances = portfolio.cashBalances.map((balance) => {
    const row = cashRow(balance);
    return {
      currency: row.currency,
      currencySymbol: row.currency,
      availableForTrading: row.availableForTrading,
      availableForWithdrawal: row.availableForWithdrawal,
      totalBalance: row.cashBalance,
      cashBalance: row.cashBalance,
      reservedForOrders: row.totalReserved,
      pendingSettlements: row.pendingSettlements,
    };
  });
  const preferred = balances.find((balance) => balance.currency && balance.currency === portfolio.currency);
  const ordered = preferred
    ? [preferred, ...balances.filter((balance) => balance !== preferred)]
    : balances;
  return {
    balances: ordered,
    totalAvailable: portfolio.totalAvailableForTrading,
    primaryCurrency: portfolio.currency || preferred?.currency || '',
  };
};

/** Stable list-row key. Not necessarily the id the portfolio route accepts. */
export const portfolioRowKey = (customer) => {
  if (customer == null) return '';
  if (typeof customer === 'string' || typeof customer === 'number') return String(customer).trim();
  return String(customer.customerId || customer.userId || customer.id || customer.email || '').trim();
};

/**
 * Ids worth trying against GET /v1/portfolio/customers/{id}.
 * Users-list `customerId` is the directory record id (often `cust_…`).
 * The portfolio route on alpha accepts the customer UUID, which is `userId`
 * on that same record when the two differ. `id` is last so a stray row id
 * never wins over userId. A string is already a single explicit id.
 */
export const portfolioIdCandidates = (customer) => {
  if (customer == null) return [];
  if (typeof customer === 'string' || typeof customer === 'number') {
    const id = String(customer).trim();
    return id ? [id] : [];
  }
  const seen = new Set();
  const candidates = [];
  [customer.customerId, customer.userId, customer.id].forEach((value) => {
    if (value == null) return;
    const id = String(value).trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    candidates.push(id);
  });
  return candidates;
};

/** Portfolio rejected this id. A 200 empty portfolio is not a miss. */
export const isPortfolioCustomerMiss = (error) => {
  const status = error?.response?.status ?? error?.status;
  return status === 404 || status === 400;
};

/**
 * Load the consolidated portfolio for a directory record or a raw id.
 * Falls through to the next candidate only when the gateway rejects the id.
 * Any other error is raised. When displayCurrency is set, a non-404 failure
 * is retried once without it before the candidate is abandoned.
 */
export const resolveCustomerPortfolio = async (fetchPortfolio, customer, options = {}) => {
  const candidates = portfolioIdCandidates(customer);
  if (!candidates.length) {
    const error = new Error('Customer id is missing');
    error.status = 400;
    throw error;
  }

  const displayCurrency = options?.displayCurrency;
  let lastError;

  for (const customerId of candidates) {
    try {
      const portfolio = await fetchPortfolio(customerId, displayCurrency ? { displayCurrency } : {});
      return { customerId, portfolio };
    } catch (error) {
      lastError = error;
      const missed = isPortfolioCustomerMiss(error);
      const notFound = (error?.response?.status ?? error?.status) === 404;
      if (displayCurrency && !(missed && notFound)) {
        try {
          const portfolio = await fetchPortfolio(customerId, {});
          return { customerId, portfolio };
        } catch (retryError) {
          lastError = retryError;
          if (!isPortfolioCustomerMiss(retryError)) throw retryError;
        }
      } else if (!missed) {
        throw error;
      }
    }
  }

  throw lastError;
};
